import styled from 'styled-components'
import mapboxgl from 'mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { useContext, useEffect, useRef, useState } from 'react'
import { point, area, center, polygon } from '@turf/turf'
import MapboxGeocoder from '../GeoCoder'
//  import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import { SquareDataDisplay } from 'components/SquareDataDisplay'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { LanguageContext } from 'components/MainComponent/MainComponent'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

const tenantCenters: { [key: string]: [number, number] } = {
  zurich: [8.524725259838222, 47.37563635920908],
  basel: [7.599006310247292, 47.552088492294686],
  bern: [7.444796713258768, 46.94548785760394],
  genf: [6.130907163612075, 46.20178799113704],
  thun: [7.6206848669241305, 46.74496568214051],
  winthi: [8.733141677236034, 47.498542671568764],
}

const tenantToPlace: { [key: string]: string } = {
  derbund: 'bern',
  tagesanzeiger: 'zurich',
  bernerzeitung: 'bern',
  berneroberlaender: 'bern',
  bazonline: 'basel',
  thunertagblatt: 'thun',
  langenthalertagblatt: 'zurich',
  zsz: 'zurich',
  landbote: 'winthi',
  zuonline: 'zurich',
  tdg: 'genf',
  '24heures': 'genf',
}

type DensityLayerProps = {
  RELI: number
  pop: number
  pop_1km: number
  coords: string
}
const minZoomForPopup = 11.9
const minZoomForHover = 12
const squareZoom = 14.5
const squareZoomMobile = 14.05
const minZoom = 7.5
const minZoomMobile = 6
const popUpText = '#202346'
const hektarLayer = 'popdens'
const hektarLayerLines = 'popdens_line'
const kilometerLayer = 'kilometer'
const emptyPolygon = {
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'Polygon',
    coordinates: [[]],
  },
}

mapboxgl.accessToken =
  'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'

// CREATE SOURCES AND LAYERS
function createSourcesAndLayers(map: mapboxgl.Map) {
  if (!map.getSource('squaremarker')) {
    map.addSource('squaremarker', {
      type: 'geojson',
      data: point([0, 0]),
    })
    map.addLayer({
      id: 'squaremarker',
      type: 'line',
      source: 'squaremarker',
      layout: {
        'line-cap': 'round',
      },
      paint: {
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1, 12, 2, 18, 6],
        'line-color': popUpText,
        'line-dasharray': [0.1, 1.5],
      },
    })

    map.addSource('hover', {
      type: 'geojson',
      data: point([0, 0]),
    })
    map.addLayer({
      id: 'hover',
      type: 'line',
      source: 'hover',
      paint: {
        'line-color': '#202346',
        'line-opacity': 1,
        'line-width': 4,
      },
    })
  }

  return [
    map.getSource('hover') as mapboxgl.GeoJSONSource,
    map.getSource('squaremarker') as mapboxgl.GeoJSONSource,
  ]
}
// TOGGLE LAYER
function toggleLayer(map: mapboxgl.Map, layer: string, visible: boolean) {
  if (map.getLayer(layer)) {
    map.setLayoutProperty(layer, 'visibility', visible ? 'visible' : 'none')
  }
}
// TOGGLE DARK MODE
function toggleDMLayers(map: mapboxgl.Map, visible: boolean) {
  toggleLayer(map, 'water_dm', visible)
  toggleLayer(map, 'background_dm', visible)
  toggleLayer(map, 'countries_dm', visible)
  toggleLayer(map, 'countries_ch_dm', visible)
}
// PARSE COORDINATES FROM STRING
function parseCoords(coords: string) {
  return [coords.split('/').map((m: any) => m.split('_').map(Number))]
}
// POLYGON FROM COORDINATES
function polygonFromCoords(coords: number[][][] | string, props?: any) {
  if (coords && typeof coords === 'string') {
    let c = parseCoords(coords as string)
    return polygon(c, props)
  }
  return polygon(coords as number[][][], props)
}
// CREATE GEOCODER
function createGeocoder(
  map: mapboxgl.Map,
  onResult: (e: any) => void,
  container: HTMLDivElement,
  width: number,
  lang: string = 'de'
) {
  console.log(mapboxgl)
  const geocoder = new MapboxGeocoder({
    accessToken: mapboxgl.accessToken,
    mapboxgl: mapboxgl as any,
    enableGeolocation: true,
    marker: { color: 'black' },
    zoom: width < 712 ? 13.8 : 14.5,
    clearAndBlurOnEsc: true,
    clearOnBlur: true,
  })
  geocoder.on('result', onResult)
  geocoder.setLanguage(lang)
  geocoder.setCountries('ch')
  geocoder.setFlyTo({ speed: 2 })
  geocoder.setFilter((e: any) => {
    return (
      !e.place_type.includes('region') &&
      !e.place_type.includes('place') &&
      !e.place_type.includes('locality')
    )
  })

  container.innerHTML = ''
  container.appendChild(geocoder.onAdd(map))
  let i = container.getElementsByTagName('input')[0]
  if (i && geocoder) {
    geocoder.setCustomInput(i)
  }
  return geocoder
}

// GET SQUARE FOR HOVER OR MARKER

function getSquareData(map: mapboxgl.Map, point: any) {
  const features = map
    .queryRenderedFeatures(point)
    .filter((f) => f.properties && f.properties.coords)
    .sort((a, b) => area(b) - area(a))
  if (features[0] && features[0].properties && features[0].properties.coords) {
    let markPoly = polygonFromCoords(
      features[0].properties.coords,
      features[0].properties
    )
    return markPoly
  }
  return
}
// MOVE KILOMETER SQUARE AND UPDATE POPUP

function moveSquareKilometerSquare(
  feature: any,
  map: mapboxgl.Map,
  setSquareData: (a: any) => void,
  width: number,
  marker?: mapboxgl.GeoJSONSource
) {
  const { pop_1km, pop, RELI } = feature.properties as DensityLayerProps
  if (RELI && map) {
    const isMobile = width < 712
    const centerPoint = center(feature)
    const cent = centerPoint.geometry.coordinates
    const currentZoom = map.getZoom()
    const isAboveSquareZoom =
      currentZoom > (isMobile ? squareZoomMobile : squareZoom)

    map.setCenter(cent as any)

    if (isAboveSquareZoom) {
      map.setZoom(isMobile ? squareZoomMobile : squareZoom)
    }

    setSquareData({ pop, pop_1km, RELI })

    map.setFilter(kilometerLayer, [
      'all',
      ['match', ['get', 'RELI'], [RELI], true, false],
    ])

    marker && marker.setData(feature)
  }
}
// MAP
export const MapboxMap = ({
  containerWidth,
  windowHeight,
  colorMode,
  tenant,
  setSelectedKmPop,
}: {
  containerWidth: number
  windowHeight: number
  colorMode: 'light' | 'dark' | undefined
  tenant: string
  setSelectedKmPop: (a: number) => void
}) => {
  const isMobile = containerWidth < 712
  const mapContainer = useRef<HTMLDivElement>(undefined as any)
  const map = useRef<mapboxgl.Map | null>(null)
  const [zoom, setZoom] = useState(isMobile ? 12.1 : 13)
  const [displayHidden, setDisplayHidden] = useState(false)
  const [mapIsActive, setMapIsActive] = useState(false)
  const [squareData, setSquareData] = useState<{
    pop: number
    pop_1km: number
    RELI: number
  } | null>(null)

  const SearchContainer = useRef<HTMLDivElement>(null)

  // HIDE AND SHOW SQUARE KILOMETER
  function hideSquareKilometerInfos(map: mapboxgl.Map) {
    toggleLayer(map, 'squaremarker', false)
  }
  function showSquareKilometerInfos(map: mapboxgl.Map) {
    toggleLayer(map, 'squaremarker', true)
  }
  useEffect(() => {
    if (squareData) setSelectedKmPop(squareData.pop_1km)
  }, [squareData, setSelectedKmPop])

  useEffect(() => {
    if (!map.current) return
    toggleDMLayers(map.current, colorMode === 'dark')
  }, [colorMode])

  useEffect(() => {
    if (!map.current) return
    let _map = map.current

    if (mapIsActive) {
      _map.dragRotate.disable()
      _map.touchZoomRotate.enable()
      _map.touchZoomRotate.disableRotation()
      _map.touchPitch.enable()
      _map.scrollZoom.enable()
      _map.dragPan.enable()
    } else {
      mapContainer.current.scrollIntoView({ behavior: 'auto' })
      _map.dragRotate.disable()
      _map.touchZoomRotate.disable()
      _map.touchPitch.disable()
      _map.scrollZoom.disable()
      _map.dragPan.disable()
    }
  }, [mapIsActive])

  // INIT MAP
  useEffect(() => {
    if (map.current) return // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/ta-interaktiv/clefrxqe0001s01sytfmkqdu3',
      center: tenantCenters[tenantToPlace[tenant]],
      zoom: zoom,
      maxZoom: 16.9,
      interactive: true,
      minZoom: containerWidth < 712 ? minZoomMobile : minZoom,
      maxBounds: [5.777703, 45.729568, 10.545769, 47.846706],
      projection: {
        name: 'equalEarth',
        center: [8.165588, 46.80194],
        parallels: [0, -90],
      } as any,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // SET MAP FUNCTIONALITIES ON MAP LOAD
  useEffect(() => {
    if (!map.current) return

    map.current.on('load', () => {
      if (map.current !== null) {
        let _map = map.current
        // DISABLE MAP ROTATION
        _map.dragRotate.disable()
        _map.touchZoomRotate.disable()
        _map.touchPitch.disable()
        _map.scrollZoom.disable()
        _map.dragPan.disable()
        if (_map.getLayer('swisscities-main')) {
          _map.setLayoutProperty('swisscities-main', 'text-field', [
            'to-string',
            ['get', Locales.lang === 'fr' ? 'name_fr' : 'nam'],
          ])
        }

        // _map.setZoom(12.1)
        // _map.setCenter(tenantCenters[tenantToPlace[tenant]])

        // TOGGLE COLORMODE ON LOAD
        toggleDMLayers(_map, colorMode === 'dark')

        // MARK SQUARE AFTER FLYING TO SEARCH RESULT
        function handleMoveEndSearch(e: any) {
          _map.off('moveend' as any, hektarLayer, handleMoveEndSearch)
          let p = _map.project(_map.getCenter())
          let features = _map.queryRenderedFeatures(p)
          if (features.length) {
            moveSquareKilometerSquare(
              features[0],
              _map,
              setSquareData,
              containerWidth,
              sMarker
            )
          }
        }

        // ON RESULT HANDLER FOR GEOCODER
        function geocoderOnResult(e: any) {
          // geocoder && geocoder.clear()
          _map.on('moveend' as any, hektarLayer, handleMoveEndSearch)
        }

        // CREATE SQUARE LAYERS
        const [sHover, sMarker] = createSourcesAndLayers(_map)

        _map.getCanvas().onclick = (e: any) => {
          let target = e.target as any
          let box = target.getBoundingClientRect()
          let pos = [e.clientX - box.left, e.clientY - box.top]
          if (map.current !== null && map.current.getZoom() > minZoomForPopup) {
            let markrect = getSquareData(map.current, pos)
            sHover.setData(emptyPolygon as any)

            moveSquareKilometerSquare(
              markrect,
              map.current,
              setSquareData,
              containerWidth,
              sMarker
            )
          }
        }

        // CREATE GEOCODER
        const geocoder =
          SearchContainer.current &&
          createGeocoder(
            _map,
            geocoderOnResult,
            SearchContainer.current,
            containerWidth,
            Locales.lang
          )
        geocoder?.setLocales([Locales.noResults, Locales.noGeolocation])

        // NAWVIGATION CONTROLS
        mapContainer.current.getElementsByClassName(
          'mapboxgl-ctrl-top-right'
        )[0].innerHTML = ''
        _map.addControl(
          new mapboxgl.NavigationControl({ showCompass: false }),
          'top-right'
        )

        // ON ZOOM
        _map.on('zoom' as any, [hektarLayer, hektarLayerLines], (e) => {
          let zoom = _map.getZoom()
          setZoom(zoom)
          if (zoom < minZoomForPopup) {
            setDisplayHidden(true)
            hideSquareKilometerInfos(_map)
          } else {
            setDisplayHidden(false)
            showSquareKilometerInfos(_map)
          }
        })

        // ON HOVER
        if (!isMobile) {
          map.current.on('mousemove', [hektarLayer], (e) => {
            if (
              map.current !== null &&
              map.current.getZoom() > minZoomForHover
            ) {
              let hoverRect = getSquareData(map.current, e.point)
              if (hoverRect) sHover.setData(hoverRect)

              map.current.getCanvas().style.cursor = 'pointer'
            }
          })

          // ON OUT
          map.current.on('mouseout', hektarLayer, (e) => {
            if (map.current !== null) {
              map.current.getCanvas().style.cursor = ''
              sHover.setData(emptyPolygon as any)
            }
          })
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.current])

  const Locales = useContext(LanguageContext)

  const handleOverlayClose = () => {
    setSquareData(null)
    if (map.current) {
      if (map.current.getLayer('squaremarker')) {
        let sMarker = map.current.getSource('squaremarker') as any
        sMarker.setData(emptyPolygon as any)
      }
      map.current.setFilter(kilometerLayer, [
        'all',
        ['match', ['get', 'RELI'], [0], true, false],
      ])
    }
  }
  return (
    <>
      <TextWidth>
        <Title>{Locales.mapTitle}</Title>
        <ColorKey>
          <ColorKeyLabel style={{ left: '0%' }}>3</ColorKeyLabel>
          <ColorKeyLabel style={{ left: '24%' }}>150</ColorKeyLabel>
          <ColorKeyLabel style={{ left: '62%' }}>350</ColorKeyLabel>
          <ColorKeyLabel
            style={{ left: 'calc(100% - 40px)', textAlign: 'right' }}
          >
            {'>'}550
          </ColorKeyLabel>
          <img src={require('../../assets/icons_key.png')} alt='Color key' />
        </ColorKey>
      </TextWidth>
      <MapBoxWrapper
        style={{
          height: windowHeight * 0.9 + 'px',
        }}
        mapIsActive={isMobile && mapIsActive}
      >
        <SearchRow ref={SearchContainer} />
        <ButtonRow>
          <ActivateButton
            mapIsActive={mapIsActive}
            onClick={(e) => {
              var isAndroid = /(android)/i.test(navigator.userAgent)

              e.stopPropagation()
              e.bubbles = false
              if (map.current) {
                setTimeout(() => {
                  map.current && map.current.resize()
                }, 100)
                let body = document.body

                if (!mapIsActive) {
                  mapContainer.current?.scrollIntoView({
                    behavior: 'smooth',
                  })
                  if (isMobile) {
                    let headercontainer = Array.from(
                      document.getElementsByTagName('header')
                    )[0]?.parentElement?.parentElement
                    if (headercontainer) headercontainer.style.zIndex = '300'

                    !isAndroid && disableBodyScroll(mapContainer.current)
                    // body.style.overflow = 'hidden'
                    // body.style.height = '100vh'
                  }
                } else {
                  if (isMobile) {
                    let headercontainer = Array.from(
                      document.getElementsByTagName('header')
                    )[0]?.parentElement?.parentElement
                    if (headercontainer) headercontainer.style.zIndex = '401'

                    !isAndroid && enableBodyScroll(mapContainer.current)
                    // body.style.overflow = 'unset'
                    // body.style.height = 'unset'
                  }
                  mapContainer.current?.scrollIntoView({
                    behavior: 'smooth',
                  })
                }
              }
              setMapIsActive(!mapIsActive)
            }}
          >
            {mapIsActive ? Locales.deactivateMap : Locales.activateMap}
          </ActivateButton>
        </ButtonRow>
        <MapboxMapRoot
          ref={mapContainer}
          className='map-container'
          style={{ pointerEvents: mapIsActive ? 'all' : 'all' }}
        >
          {squareData && !displayHidden && (
            <SquareDataDisplay
              text={Locales.lang}
              data={squareData}
              handleOverlayClose={handleOverlayClose}
            />
          )}
        </MapboxMapRoot>
      </MapBoxWrapper>
    </>
  )
}
const ColorKeyLabel = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 12px;
  width: 40px;
  position: absolute;
  top: 0;
  text-align: left;
`
const ColorKey = styled.div`
  position: relative;
  padding-top: 15px;
  display: inline-block;
  width: 100%;
  img {
    width: 100%;
    height: 10px;
  }
`
const Title = styled.div`
  flex: 0 0 100%;
  font-family: var(--font-sans-serif);
  font-weight: 700;
  line-height: 1.2em;
  margin-bottom: 15px;
`
const BaseButton = styled.button`
  flex: 0 0 60px;
  height: 60px;
  border: 0;
  transition: transform 0.2s ease, max-width 0.2s ease;
  letter-spacing: 0.4px;
  pointer-events: all;
  overflow: hidden;
  border-radius: 4px;

  &:hover {
    cursor: pointer;
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.98);
  }
`

const MapBoxWrapper = styled.div<{ mapIsActive: boolean }>`
  z-index: 910;
  position: relative;
  width: 100%;
  margin-bottom: 30px;
  position: ${({ mapIsActive }) => (mapIsActive ? 'fixed' : 'relative')};
  top: 0;
  height: ${({ mapIsActive }) =>
    mapIsActive ? '100vh !important' : undefined}; ;
`

const ActivateButton = styled(BaseButton)<{ mapIsActive: boolean }>`
  background-color: ${({ mapIsActive }) =>
    mapIsActive ? 'var(--red-main)' : 'var(--brandblue-main)'};
  color: #fff;
  min-width: 150px;
  margin-right: 0px;
  z-index: 604;
`

const ButtonRow = styled.div`
  z-index: 1003;
  width: 100%;
  text-align: center;
  position: absolute;
  top: 80px;

  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`
const SearchRow = styled.div`
  width: 100%;
  text-align: center;
  position: absolute;
  top: 10px;
  z-index: 1004;
  pointer-events: none;
  .suggestions {
    padding: 10px !important;
  }
  .mapboxgl-ctrl-geocoder {
    font-family: var(--font-sans-serif);
    z-index: 300;
    max-width: 368px;
    text-align: left;
    border-radius: 4px;
    margin-left: auto;
    margin-right: auto;
    @media screen and (max-width: 711px) {
      margin-left: auto;
      margin-right: 50px;
      width: calc(100% - 60px);
    }
    input {
      padding-top: 10px;
      padding-bottom: 10px !important;
      height: 60px;
      @media screen and (max-width: 711px) {
        /* font-size: 12px; */
      }
    }
    svg {
      margin-top: 12px;
      @media screen and (max-width: 639px) {
        &.mapboxgl-ctrl-geocoder--icon-search {
          margin-top: 4px;
        }
      }
    }
  }
`
const MapboxMapRoot = styled.div`
  height: calc(100%) !important;
  position: relative;
  width: 100%;
  pointer-events: all;
  z-index: 1002;
  .hidden {
    display: none;
  }
  .mapboxgl-popup {
    line-height: 0;
    margin-top: 5px;
    position: absolute;
    top: 0 !important;
    left: 50%;
    transform: translateX(-50%) !important;
  }

  .mapboxgl-ctrl-attrib-inner {
    font-family: var(--font-sans-serif);
    font-size: 10px;
    padding: 0;
    line-height: 1.4em;
    background-color: var(--site-background);
    border-color: var(--site-background);
    a {
      color: var(--text-color);
    }
  }
  .mapboxgl-ctrl-attrib {
    background-color: var(--site-background);
    border-color: var(--site-background);
  }
`
